// autopilot-doctor-command.ts
//
// Split out of autopilot.ts (was 213 lines, over the 200-line budget — review
// 260909-p6-m2 INFO). Owns `avatar autopilot doctor [--reverify] [--json]`.
//
// Req 3 (D9): the environment group (claude/login/pack/config/Mattermost/
// env/GitLab PAT) runs EVEN WITH ZERO missions on disk — `resolveMission`
// used to throw immediately (Key Insight #2); 0 missions + no `--mission`
// now falls back to environment-only (autopilot-doctor-no-mission.ts).
import { join } from "node:path";
import type { Command } from "commander";
import {
  buildAutopilotDoctorJsonPayload,
  runAutopilotDoctorChecks,
} from "../lib/autopilot/autopilot-doctor.js";
import {
  environmentChecksBlockRun,
  runEnvironmentChecks,
} from "../lib/autopilot/autopilot-environment-checks.js";
import {
  loadOpsChecksDeps,
  probeClaudeVersion,
} from "../lib/autopilot/autopilot-ops-checks-cli-wiring.js";
import { resolveMission } from "../lib/autopilot/mission-resolver.js";
import { reverifyPinTuple } from "../lib/autopilot/pack-pin-drift-detector.js";
import { claudeBinOverrideNotice } from "../lib/autopilot/run-command-session-deps.js";
import { pathExists } from "../lib/filesystem-helpers.js";
import { writeMachineReadableJson } from "../lib/machine-readable-json-output-contract.js";
import { log } from "../lib/terminal-logger.js";
import { buildDoctorEnvironmentDeps } from "./autopilot-doctor-environment-deps.js";
import { isNoMissionsYetError, runEnvironmentOnlyDoctor } from "./autopilot-doctor-no-mission.js";

export interface RegisterAutopilotDoctorCommandDeps {
  resolveWorkspaceRoot: (repoFlag: string | undefined) => Promise<string>;
  defaultGlobalConfigPath: string;
}

export function registerAutopilotDoctorCommand(
  autopilot: Command,
  deps: RegisterAutopilotDoctorCommandDeps,
): void {
  autopilot
    .command("doctor")
    .description("Chẩn đoán mission autopilot")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .option(
      "--reverify",
      "Xác nhận lại tuple pack pin/claude binary/settings sau khi đã kiểm tra thủ công (§18 pin drift)",
    )
    .action(
      async (opts: { mission?: string; repo?: string; json?: boolean; reverify?: boolean }) => {
        const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);

        let resolved: Awaited<ReturnType<typeof resolveMission>> | undefined;
        try {
          resolved = await resolveMission({ workspaceRoot, explicitMissionId: opts.mission });
        } catch (err) {
          // Req 3: 0 missions + no --mission => environment-only, not an
          // error. Any OTHER resolution error still throws as before.
          if (!isNoMissionsYetError(err, opts.mission)) throw err;
        }

        if (resolved === undefined) {
          await runEnvironmentOnlyDoctor(
            workspaceRoot,
            deps.defaultGlobalConfigPath,
            Boolean(opts.json),
          );
          return;
        }

        const missionWorkspace = join(resolved.missionHome, "workspace");
        const packRoot = join(missionWorkspace, ".claude", "pack");

        if (opts.reverify) {
          const { tuple, receipt } = await reverifyPinTuple({
            missionWorkspace,
            controlRoot: resolved.controlRoot,
            probeClaudeVersion,
          });
          if (opts.json) {
            writeMachineReadableJson({ command: "autopilot doctor --reverify", tuple, receipt });
          } else {
            log.info(`Tuple đã xác nhận lại lúc ${receipt.reverified_at}.`);
            log.info(
              receipt.changed_fields.length > 0
                ? `Trường đã đổi: ${receipt.changed_fields.join(", ")}`
                : "Không có trường nào đổi so với lần kiểm trước.",
            );
          }
          return;
        }

        // opsChecks with REAL openAttemptIds — never fail-open `?? []` (H2).
        const { effectiveConfig, opsChecks } = await loadOpsChecksDeps(
          deps.defaultGlobalConfigPath,
          resolved.controlRoot,
        );
        const result = await runAutopilotDoctorChecks({
          missionWorkspace,
          packRoot: (await pathExists(packRoot))
            ? packRoot
            : join(workspaceRoot, ".claude", "pack"),
          controlRoot: resolved.controlRoot,
          opsChecks,
        });
        // AC-09: a malformed/unreadable global config must surface as its
        // own failing check, not silently seed defaults for every OTHER
        // check to report against.
        if (!effectiveConfig.ok) {
          result.checks.unshift({
            name: "config",
            status: "fail",
            detail: `config_invalid — ${effectiveConfig.message}`,
          });
          result.blocksRun = true;
        }
        // Req 3: environment group STILL runs alongside the per-mission
        // group — a mission's own checks say nothing about `claude` itself.
        if (effectiveConfig.ok) {
          const envChecks = await runEnvironmentChecks(
            await buildDoctorEnvironmentDeps({
              workspaceRoot,
              config: effectiveConfig.config,
              mission: { workspace: missionWorkspace, controlRoot: resolved.controlRoot },
              unusedConfigKeyWarnings: effectiveConfig.unusedKeyWarnings,
            }),
          );
          result.checks.push(...envChecks);
          result.blocksRun = result.blocksRun || environmentChecksBlockRun(envChecks);
        }
        const claudeBinNotice = claudeBinOverrideNotice();
        if (opts.json) {
          writeMachineReadableJson({
            ...buildAutopilotDoctorJsonPayload(result),
            claude_bin_override: claudeBinNotice ?? null,
          });
        } else {
          if (claudeBinNotice) log.info(claudeBinNotice);
          for (const c of result.checks) log.info(`[${c.status}] ${c.name}: ${c.detail}`);
          if (result.blocksRun) log.error("Doctor chặn 'run' — xem check ở trên.");
        }
        // Req 4: any `fail` check ⇒ exit 1, in --json too (previously always
        // exit 0 even when the text said "Doctor chặn 'run'").
        if (result.blocksRun) process.exitCode = 1;
      },
    );
}
